export async function fitImageToExactSizeIfNeeded(
  dataUrl: string,
  targetWidth: number,
  targetHeight: number,
  threshold = 0.015
): Promise<string> {
  const img = await loadImage(dataUrl);

  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  if (!srcW || !srcH) return dataUrl;

  const targetAspect = targetWidth / targetHeight;
  const srcAspect = srcW / srcH;
  const aspectDelta = Math.abs(srcAspect - targetAspect) / targetAspect;

  // Already correct size and close enough aspect ratio
  if (srcW === targetWidth && srcH === targetHeight && aspectDelta < threshold) {
    return dataUrl;
  }

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;

  // If aspect ratio is close, just rescale to exact pixels.
  if (aspectDelta < threshold) {
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
    return canvas.toDataURL('image/png');
  }

  // Otherwise, create a "smart fill" background (blurred cover) and place the image centered (contain)
  const coverScale = Math.max(targetWidth / srcW, targetHeight / srcH);
  const coverW = srcW * coverScale;
  const coverH = srcH * coverScale;
  const coverX = (targetWidth - coverW) / 2;
  const coverY = (targetHeight - coverH) / 2;

  ctx.filter = 'blur(24px)';
  ctx.drawImage(img, coverX, coverY, coverW, coverH);

  ctx.filter = 'none';
  const containScale = Math.min(targetWidth / srcW, targetHeight / srcH);
  const containW = srcW * containScale;
  const containH = srcH * containScale;
  const containX = (targetWidth - containW) / 2;
  const containY = (targetHeight - containH) / 2;
  ctx.drawImage(img, containX, containY, containW, containH);

  return canvas.toDataURL('image/png');
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}
